import useServiceQuoteFlow from "./hooks/useServiceQuoteFlow";
import ZipStep from "./steps/ZipCodeStep";
import AdditionalInfoStep from "./steps/AdditionalInfoStep";
import HomeownerNameStep from "./steps/HomeownerNameStep";
import HomeownerAddressStep from "./steps/HomeownerAddressStep";
import HomeownerPhoneStep from "./steps/HomeownerPhoneStep";
import QuestionStep from "./steps/QuestionStep";
import { isMobile } from "@/utils/is-mobile";
import { useEffect, useRef } from "react";
import useReferenceScroller from "./hooks/useReferenceScroller";
import useEffectOnce from "@/hooks/useEffectOnce";
import { animate } from "framer-motion";
import { trackNetworxPage } from "@/utils/tracking/track-networx-page";
import usePHPDoctor from "./hooks/usePHPDoctor";
import useStepper from "./hooks/useStepper";
import * as Sentry from "@sentry/react";
import ProgressBar from "./components/ProgressBar";
import { cn } from "@networx/utils/tailwind/cn";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Virtual } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
// NOTE: we don't need this imporant in case this Version of AB-Test is not a "Clean Design" from PHP
// i.e. PHP is injecting those class for some AB Tests this one included
// import 'swiper/css';
import { IIFE } from "@/utils/functional";
import getCurrenQueryParams from "@/utils/get-current-query-params";
import { useDeferredCallback } from "@/hooks/use-deferred-callback";

export default function ServiceQuoteFlow() {
  //---- DOM CLEANUP
  // NOTE: this is because we have a theme (in tailwind.config.js) that is called "service-quote-flow"
  // we must remove it to use "service-quote-flow-rewrite"
  usePHPDoctor({ bodyClassesToRemove: ["service-quote-flow"] });

  useEffectOnce(() => {
    // NOTE: before react (js) kick in we need to display a placeholder space
    const appNode = document.querySelector("#app");
    if (appNode) {
      (appNode as HTMLDivElement).style.height = "unset";
    }

    // NOTE: for the sticky progress bar to work we need to remove his ancestor that have a overflow attrib
    const sIntro = document.querySelector("#s-intro");
    if (sIntro) (sIntro as HTMLDivElement).style.overflow = "unset";
  });

  const togglePHPHeader = (value: boolean) => {
    const headerWrap = document.querySelector(".pageWrap > .headerWrap");
    if (headerWrap) (headerWrap as HTMLDivElement).style.visibility = value ? "visible" : "hidden";
  }
  //---- END of DOM CLEANUP

  const isMobileView = isMobile();
  const showStepper = isMobileView;

  const swiperRef = useRef<SwiperType>();

  //---- CORE LOGIC
  const {
    steps,
    form,
    tcpaText,
    trustedFormRef,
    getPageNameByNodeId,
    isLastStep,
    isFirstStep,
    trackPrev,
  } = useServiceQuoteFlow({
    onNext: (nodeId) => {
      const currentIndex = steps.findIndex(s => s.nodeId === nodeId);
      // The timeout in this case is for the user to see what he selected before jumping to next screen
      setTimeout(() => {
        const nextIndex = currentIndex === -1 ? undefined : currentIndex;
        next(nextIndex);
        swiperRef.current?.slideTo(nextIndex ? nextIndex + 1 : 1);
      }, 300);
    }
  });
  //----- END of CORE LOGIC


  //---- TRACKING
  const trackPage = (nodeId: string) => {
    console.log("Page Track", getPageNameByNodeId(nodeId));
    try {
      trackNetworxPage(getPageNameByNodeId(nodeId));
    } catch (err) {
      console.warn("trackPage failed.", err);
    }
  }
  //----- END of TRACKING

  const deferCallbackForSteps = useDeferredCallback(steps, trackPage);

  //---- STEPPER
  const { currentStep, currentStepIndex, stepIndex, next, previous, setStepIndex } = useStepper({
    steps,
    onPrevious: (current, target) => {
      if (steps[current]) {
        trackPrev(steps[current].nodeId);
        swiperRef.current?.slideTo(target);
      } else {
        Sentry.captureMessage(`ServiceQuoteFlow::useStepper::onPrevious -- 'current' index (${current}) is out of bounds of 'steps' (len '${steps.length}') `, "warning");
      }
    },
    onNext: (current, target) => {
      console.log("onNext", { steps, current: steps[current]?.nodeId, target: steps[target]?.nodeId })
      if (steps[target]) {
        // If we have it (mean we did prev then next) we track the page
        trackPage(steps[target].nodeId);
      } else {
        // else we wait for next re-render of react for the new steps
        deferCallbackForSteps((steps, trackPage) => {
          console.log("onNext::defer", { steps: steps, current: steps[current]?.nodeId, target: steps[target]?.nodeId })
          if (steps[target]) {
            trackPage(steps[target].nodeId);
          } else {
            Sentry.captureMessage(`ServiceQuoteFlow::useStepper::onNext -- 'current' index (${current}) is out of bounds of 'steps' (len '${steps.length}') `, "warning");
          }
        })

      }
    }
  });
  useEffect(() => {
    if (showStepper) {
      togglePHPHeader(currentStepIndex === 0)
    }
  }, [currentStepIndex]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStepIndex]);



  const progress = IIFE(() => {
    const staticStepsIds = [
      !getCurrenQueryParams().zip && "zip",
      "581",
      "additional-info",
      "homeowner-full-name",
      "homeowner-address",
      "homeowner-phone",
    ].filter(Boolean);

    const stepsQNA = steps.filter(step => !staticStepsIds.includes(step?.id!));
    const total = staticStepsIds.length + stepsQNA.length;
    return (currentStepIndex / total);
  });
  useEffect(() => {
    // NOTE: we have a huge footer used for SEO, we show it only on the first step
    // after the first "next" click we remove it until the end of the flow (redirect)
    if (!progress) return;

    const serviceContent = document.querySelector("#service-content");
    if (serviceContent) {
      (serviceContent as HTMLDivElement).style.display = "none";
    }

    const promoBoxes = document.querySelector(".promo-boxes");
    if (promoBoxes) {
      (promoBoxes as HTMLDivElement).style.display = "none";
    }
  }, [Boolean(progress)]);
  //---- END of STEPPER

  //----- SCROLLER
  const { registerStep, getStepElement } = useReferenceScroller();

  const SCROLL_ANIMATION_DELAY_SECONDS = 0.3;
  const SCROLL_ANIMATION_DURATION_SECONDS = 0.8;
  const smoothScrollToElement = (elementId: string) => {
    const element = getStepElement(elementId);
    if (!element) return;

    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.scrollY - 50; // 50px offset from top

    animate(window.scrollY, offsetPosition, {
      duration: SCROLL_ANIMATION_DURATION_SECONDS,
      onUpdate: (value) => {
        window.scrollTo({
          top: value,
        });
      },
    });
  };

  useEffect(() => {
    // On Desktop we have a vertical scroller, each time we have a new stepIndex
    // we want to scroll automatically to it
    if (showStepper || stepIndex.index === 0) return;
    setTimeout(() => smoothScrollToElement(currentStep.nodeId), SCROLL_ANIMATION_DELAY_SECONDS / 1000);
  }, [stepIndex])
  //----- END of SCOLLER

  //----- SWIPER / CAROUSEL
  // const { containerProps, itemProps } = useFramerMotionCarousel({
  //   index: currentStepIndex,
  //   onNext: next,
  //   onPrevious: previous,
  // });
  //----- END of SWIPER / CAROUSEL

  //----- RENDER
  const getScreen = (step: typeof steps[number], isFocused: boolean) => {
    const showPrevButton = !isFirstStep(step.nodeId)
      && !isLastStep(step.nodeId)
      && step.type !== "homeowner-full-name"
      && step.type !== "homeowner-address"
      && step.type !== "homeowner-phone";
    const props = {
      step,
      form,
      onClickPrev: showPrevButton ? previous : undefined,
      isFocused,
    };
    switch (step.type) {
      case "zip": {
        // @ts-expect-error same
        return <ZipStep {...props} />
      }
      case "QNA": {
        // @ts-expect-error same
        return <QuestionStep {...props} />;
      }
      case "additional-info": {
        // @ts-expect-error same
        return <AdditionalInfoStep {...props} />
      }
      case "homeowner-full-name": {
        // @ts-expect-error same
        return <HomeownerNameStep {...props} />
      }
      case "homeowner-address": {
        // @ts-expect-error same
        return <HomeownerAddressStep {...props} />
      }
      case "homeowner-phone": {
        // @ts-expect-error same
        return <HomeownerPhoneStep
          {...props}
          tcpaText={tcpaText}
        />
      }
      default: {
        //@ts-ignore type says it's never, but in case you developing it will catch it
        throw new Error(`RENDER: ${step.type} type is not implemented`);
      }
    }
  }

  return (
    <div
      className={cn(
        "bg-background h-full w-full flex flex-col", showStepper && "overflow-x-hidden",
      )}>
      {/* LOADING BAR */}
      {(!showStepper ? steps.length > 1 : currentStepIndex !== 0) && (
        <div className={cn(
          "w-full max-w-screen p-4 bg-[#435b6c] flex justify-center items-center",
          "z-50 top-0",
          showStepper ? "fixed" : "sticky",
        )} style={{
          height: PROGRESS_BAR_HEIGHT,
          maxHeight: PROGRESS_BAR_HEIGHT,
        }}>
          <ProgressBar
            value={parseInt((progress * 100).toFixed(0))}
            className="max-w-2xl"
            classNames={{
              progress: "bg-[#5DC083]"
            }}
          />
        </div>
      )}

      {/* TRUSTED FORM */}
      <form
        className="hidden"
        ref={trustedFormRef}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          // we will submit programaticlly not with a button
        }}
        data-localdev={!document.querySelector(".pageWrap .headerWrap")}
        className={cn(
          "flex flex-col justify-start w-full font-display2",
          !showStepper && "min-h-screen gap-[40px]",
          // NOTE: In Mobile we show the header nav bar in the first step then the progress bar is on top of it
          showStepper && "gap-0 min-h-[calc(100svh-50px)] data-[localdev=true]:mt-[50px]",
          !showStepper && ["mt-8"],
        )}
      >
        {showStepper && (
          <Swiper
            modules={[Virtual]}
            className="flex cursor-grab active:cursor-grabbing"
            slidesPerView={1}
            spaceBetween={0}
            scrollbar={{
              draggable: true,
            }}
            virtual
            allowTouchMove
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
            onSlideChange={(swiperType) => {
              // TODO: sync w/ history navigation
              // TODO: when swiping we should trigger a prev/next event for tracking
              setStepIndex({ index: swiperType.activeIndex });
            }}
          >
            {steps.map((step, index) => (
              <SwiperSlide
                key={step.id}
                className="flex !min-h-[calc(100svh-50px)] !h-[calc(100svh-50px)] shrink-0 w-dvw"
                virtualIndex={index}
              >
                {getScreen(step, index === currentStepIndex)}
              </SwiperSlide>
            ))}
          </Swiper>
        )}
        {!showStepper && steps.map((step, index, { length }) => {
          const isLast = length - 1 === index;
          return (
            <div
              ref={registerStep(step.nodeId)}
              className={cn("flex flex-col w-full items-center", isLast && LAST_DESKTOP_STEP_CLASSNAME)}
              key={step.nodeId}
            >
              {getScreen(step, index === currentStepIndex)}
            </div>
          )
        })}
      </form>
    </div >
  );
}


const PROGRESS_BAR_HEIGHT = 58;
const LAST_DESKTOP_STEP_CLASSNAME = "min-h-[calc(100svh-58px)]" satisfies `min-h-[calc(100svh-${typeof PROGRESS_BAR_HEIGHT}px)]`;
